/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import {
  chequeNoValidations,
  chequeBSBValidations,
  chequeAccountNoValidations,
  moneyOrderNumberValidations,
  moneyOrderTextValidations,
} from 'linced-app/utils/finance';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import LineItemAdjustmentMode from 'linced-app/enums/line-item-adjustment-mode';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import ENV from 'linced-app/config/environment';
import moment from 'moment';
import { next } from '@ember/runloop';
import { rejectBy } from '@linc-technologies/linced-common/utils/arrays';

function sumReducer(field) {
  return (sum, item) => {
    return sum + item[field];
  };
}

export default class LineItemList extends Component {
  @action
  setAdjustBy(value) {
    this.adjustBy = value;
  }

  @action
  setAdjustTo(value) {
    this.adjustTo = value;
  }

  @action
  setAdjustmentMode(value) {
    this.adjustmentMode = value;
  }

  @action
  setItemCostCentre(value) {
    this.itemCostCentre = value;
  }

  @action
  setItemDate(value) {
    this.itemDate = value;
  }

  @action
  setItemDescription(value) {
    this.itemDescription = value;
  }

  @action
  setItemDescriptionViolations(value) {
    this.itemDescriptionViolations = value;
  }

  @action
  setItemInternalOrder(value) {
    this.itemInternalOrder = value;
  }

  @action
  setItemPayerName(value) {
    this.itemPayerName = value;
  }

  @action
  setPaymentAmount(value) {
    this.paymentAmount = value;
  }

  @action
  setPaymentChequeAccountNo(value) {
    this.paymentChequeAccountNo = value;
  }

  @action
  setPaymentChequeBSB(value) {
    this.paymentChequeBSB = value;
  }

  @action
  setPaymentChequeNo(value) {
    this.paymentChequeNo = value;
  }

  @action
  setPaymentComment(value) {
    this.paymentComment = value;
  }

  @action
  setPaymentCommentViolations(value) {
    this.paymentCommentViolations = value;
  }

  @action
  setPaymentDate(value) {
    this.paymentDate = value;
  }

  @action
  setPaymentMoneyOrderIssuer(value) {
    this.paymentMoneyOrderIssuer = value;
  }

  @action
  setPaymentMoneyOrderNumber(value) {
    this.paymentMoneyOrderNumber = value;
  }

  @action
  setPaymentMoneyOrderPayee(value) {
    this.paymentMoneyOrderPayee = value;
  }

  @action
  setVoidWriteoffType(value) {
    this.voidWriteoffType = value;
  }

  LineItemAdjustmentMode = LineItemAdjustmentMode;

  // Services
  @service
  features;

  @service
  fetch;

  @service
  finance;

  @service
  paperToaster;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  strings;

  @alias('session.school')
  school;

  // NSW DoE prohibited financial language (SMOS-012).
  // itemDescription is the only editable text field on the edit dialog
  // (itemName is immutable after creation).
  // Populated by Finance::ProhibitedLanguageAlert via @onChange.
  @tracked
  itemDescriptionViolations = [];

  @tracked
  paymentCommentViolations = [];

  @computed('itemDescriptionViolations.[]', 'paymentCommentViolations.[]')
  get hasLanguageViolations() {
    return (
      !isEmpty(this.itemDescriptionViolations) ||
      !isEmpty(this.paymentCommentViolations)
    );
  }

  // community determines whether the line items are for community (i.e. not
  // associated with learners' finance accounts).
  community = false;

  // costCentres allows the caller to pass in the list of Cost Centres.
  costCentres = [];

  // internalOrders allows the caller to pass in the list of Internal Orders.
  internalOrders = [];

  @computed('items.@each.displayAmount')
  get totalDebit() {
    return this.items.reduce(sumReducer('displayAmount'), 0);
  }

  @computed('items.@each.receipts', 'learnerAccount.balance')
  get totalReceipts() {
    let totalReceipts = this.items.reduce(sumReducer('receipts'), 0);
    if (!isEmpty(this.learnerAccount)) {
      totalReceipts += this.learnerAccount.balance;
    }
    return totalReceipts;
  }

  @computed('items.@each.otherCredits')
  get totalOtherCredits() {
    return this.items.reduce(sumReducer('otherCredits'), 0);
  }

  @computed('items.@each.balance')
  get subtotalBalance() {
    return this.items.reduce(sumReducer('balance'), 0);
  }

  @computed('accounts.@each.balance')
  get accountsBalance() {
    if (isEmpty(this.accounts)) {
      return 0;
    }
    return this.accounts.reduce(sumReducer('balance'), 0);
  }

  @computed('subtotalBalance', 'accountsBalance')
  get totalBalance() {
    return this.subtotalBalance - this.accountsBalance;
  }

  @tracked
  addPaymentDialogOpen = false;
  @tracked
  discountDialogOpen = false;
  @tracked
  paymentItem = null;
  @tracked
  transferAccount = null;
  @tracked
  paymentAmount = null;
  @tracked paymentMethod = null;
  @tracked
  paymentComment = '';
  @tracked
  paymentPerson = null;
  @tracked
  paymentPersonName = '';
  @tracked
  paymentDate = moment().format('YYYY-MM-DD');
  @tracked
  paymentChequeNo = '';
  @tracked
  paymentChequeBSB = '';
  @tracked
  paymentChequeAccountNo = '';
  @tracked
  paymentMoneyOrderNumber = '';
  @tracked
  paymentMoneyOrderIssuer = '';
  @tracked
  paymentMoneyOrderPayee = '';

  get tillDisabled() {
    return this.finance.tillDisabled(this.paymentMethod);
  }

  @tracked
  voidWriteoffDialogOpen = false;
  @tracked voidWriteoffType = 'writeoff';

  get voidWriteoffCommentLabel() {
    switch (this.voidWriteoffType) {
      case 'writeoff':
        return 'write off';
      case 'void':
        return 'void';
    }
    return 'adjustment';
  }

  @tracked
  editItemDialogOpen = false;
  @tracked
  editingItem = null;
  @tracked
  itemDescription = '';
  @tracked
  itemDate = moment().format('YYYY-MM-DD');
  @tracked
  itemPayerName = '';
  @tracked
  itemCostCentre = null;
  @tracked
  itemInternalOrder = null;
  @tracked
  adjustmentMode = LineItemAdjustmentMode.TO;
  @tracked
  adjustBy = 0;
  @tracked
  adjustTo = 0;

  @filterBy('costCentres', 'isActive')
  availableCostCentres;

  @filterBy('internalOrders', 'isActive')
  availableInternalOrders;

  PaymentMethod = PaymentMethod;

  @alias('finance.availablePaymentMethods')
  paymentMethods;

  chequeNoValidations = chequeNoValidations;
  chequeBSBValidations = chequeBSBValidations;
  chequeAccountNoValidations = chequeAccountNoValidations;
  moneyOrderNumberValidations = moneyOrderNumberValidations;
  moneyOrderTextValidations = moneyOrderTextValidations;
  selectable = false;
  selectedItems = [];

  @computed('selectedItems.[]', 'items.[]')
  get allSelected() {
    return rejectBy(this.items, 'isArchived').every((item) =>
      this.selectedItems.includes(item),
    );
  }

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  addItemMode;

  @tracked
  addPaymentInProgress;

  @tracked
  adjustmentInProgress;

  @tracked
  discountInProgress;

  @tracked
  errorMessage;

  @tracked
  increaseDialogOpen;

  @tracked
  itemAmount;

  @tracked
  itemEditInProgress;

  @tracked
  itemLedger;

  @tracked
  itemLedgerItem;

  @tracked
  reduceDialogOpen;

  @tracked
  voidInProgress;

  @tracked
  writeoffInProgress;

  @tracked
  addPIADialogOpen;

  @tracked
  currentYear;

  @tracked
  filter;

  @tracked
  transferPIADialogOpen;

  @tracked
  transferPIAToSiblingDialogOpen;

  @computed('jurisdiction')
  get isNSW() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @action
  noOp() {}

  @action
  setPaymentMethod(method) {
    this.paymentMethod = method;
  }

  @action
  toggleAddPaymentDialog(item) {
    this.errorMessage = '';
    this.toggleProperty('addPaymentDialogOpen');
    if (!this.addPaymentDialogOpen) {
      this.resetPaymentForm();
    } else if (item) {
      this.paymentItem = item;
      this.paymentAmount = null;
    }
  }

  @action
  setPaidBy(billPayer) {
    this.paymentPerson = billPayer;
    this.paymentPersonName = billPayer.fullName;
  }

  @action
  setPaidByName(name) {
    this.paymentPerson = null;
    this.paymentPersonName = name;
  }

  @action
  addPayment() {
    let accountId = this.get('paymentItem.account.id');
    if (this.community) {
      // Community payments don't get associated with an account.
      accountId = 0;
    }
    this.addPaymentInProgress = true;
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/accounts/${accountId}/:pay`;
    let amount = Math.round(this.paymentAmount * 100);
    let tillId;
    if (!this.tillDisabled && !isEmpty(this.get('finance.activeTill'))) {
      tillId = parseInt(this.get('finance.activeTill.id'), 10);
    }
    if (this.paymentMethod !== PaymentMethod.CHEQUE) {
      // Clear cheque detail fields if the payment isn't by cheque.
      this.paymentChequeNo = '';
      this.paymentChequeBSB = '';
      this.paymentChequeAccountNo = '';
    }
    if (this.paymentMethod !== PaymentMethod.MONEY_ORDER) {
      // Clear MO detail fields if the payment isn't by Money Order.
      this.paymentMoneyOrderNumber = '';
      this.paymentMoneyOrderIssuer = '';
      this.paymentMoneyOrderPayee = '';
    }
    let data = {
      accountId: parseInt(accountId, 10),
      method: this.paymentMethod,
      comment: this.paymentComment,
      date: this.paymentDate,
      amount,
      apportionment: [
        {
          lineItemId: parseInt(this.paymentItem.id, 10),
          amount,
          comment: this.paymentComment,
        },
      ],
      chequeNo: this.paymentChequeNo,
      chequeBSB: this.paymentChequeBSB,
      chequeAccountNo: this.paymentChequeAccountNo,
      moneyOrderNumber: this.paymentMoneyOrderNumber,
      moneyOrderIssuer: this.paymentMoneyOrderIssuer,
      moneyOrderPayee: this.paymentMoneyOrderPayee,
      tillId,
    };
    // Only personId OR personName can be set - not both.
    if (!isEmpty(this.paymentPerson)) {
      data.personId = this.get('paymentPerson.caregiverId');
    } else {
      data.personName = this.paymentPersonName;
    }
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then((response) => {
        this.paperToaster.show(`Payment added successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.paymentItem.reload();
        // Explicitly reload all transactions for this line item
        // So we keep our Receipt and Other Credit values up-to-date
        this.store.query('finance/transaction', {
          lineItemId: this.paymentItem.id,
        });
        this.addPaymentDialogOpen = false;
        this.resetPaymentForm();
        if (this.isNSW) {
          next(this, function () {
            let queryParams = {
              personId: this.learnerAccount.person_id,
            };
            this.router.transitionTo(
              'admin.quick-payment.receipt',
              response.transactionId,
              { queryParams },
            );
          });
        }
      })
      .catch(() => {
        this.paperToaster.show('Error adding Payment!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
        if (!isEmpty(tillId)) {
          // Update till sessions, in case somebody else started one for this
          // till after the payment dialog was opened.
          this.finance.updateTillSessions();
        }
      })
      .then(() => {
        this.addPaymentInProgress = false;
      });
  }

  @action
  toggleDiscountDialog(item) {
    this.errorMessage = '';
    this.toggleProperty('discountDialogOpen');
    if (!this.discountDialogOpen) {
      this.resetPaymentForm();
    } else if (item) {
      this.paymentItem = item;
      this.paymentAmount = item.balance;
    }
  }

  @action
  discount() {
    this.discountInProgress = true;
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/lineitems/${this.paymentItem.id}/:discount`;
    let amount = Math.round(this.paymentAmount * 100);
    let data = {
      lineItemId: parseInt(this.paymentItem.id, 10),
      comment: this.paymentComment,
      date: this.paymentDate,
      amount,
    };
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.paperToaster.show(`Discount applied successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.paymentItem.reload();
        // Explicitly reload all transactions for this line item
        // So we keep our Receipt and Other Credit values up-to-date
        this.store.query('finance/transaction', {
          lineItemId: this.paymentItem.id,
        });
        this.discountDialogOpen = false;
        this.resetPaymentForm();
      })
      .catch(() => {
        this.paperToaster.show('Error applying discount!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.discountInProgress = false;
      });
  }

  @action
  toggleVoidWriteoffDialog(item) {
    this.errorMessage = '';
    this.toggleProperty('voidWriteoffDialogOpen');
    if (!this.voidWriteoffDialogOpen) {
      this.resetVoidWriteoffForm();
    } else if (item) {
      this.paymentItem = item;
    }
  }

  @action
  writeoff() {
    this.writeoffInProgress = true;
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/lineitems/${this.paymentItem.id}/:writeoff`;
    let data = {
      lineItemId: parseInt(this.paymentItem.id, 10),
      comment: this.paymentComment,
      date: this.paymentDate,
    };
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.paperToaster.show(`Line item written off successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.paymentItem.reload();
        // Explicitly reload all transactions for this line item
        // So we keep our Receipt and Other Credit values up-to-date
        this.store.query('finance/transaction', {
          lineItemId: this.paymentItem.id,
        });
        this.voidWriteoffDialogOpen = false;
        this.resetVoidWriteoffForm();
      })
      .catch(() => {
        this.paperToaster.show('Error writing off line item!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.writeoffInProgress = false;
      });
  }

  @action
  voidLineItem() {
    this.voidInProgress = true;
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/lineitems/${this.paymentItem.id}/:void`;
    let data = {
      lineItemId: parseInt(this.paymentItem.id, 10),
      comment: this.paymentComment,
      date: this.paymentDate,
    };
    let action = 'voided';
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.paperToaster.show(`Line item ${action} successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.paymentItem.reload();
        // Explicitly reload all transactions for this line item
        // So we keep our Receipt and Other Credit values up-to-date
        this.store.query('finance/transaction', {
          lineItemId: this.paymentItem.id,
        });
        this.voidWriteoffDialogOpen = false;
        this.resetVoidWriteoffForm();
      })
      .catch(() => {
        this.paperToaster.show('Error voiding line item!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.voidInProgress = false;
      });
  }

  // editItemOpening is the item whose dialog opens next. A later open
  // replaces it, so an earlier load does not fill the form.
  #editItemOpening = null;

  @action
  async toggleEditItemDialog(item) {
    this.errorMessage = '';
    if (this.editItemDialogOpen) {
      this.#editItemOpening = null;
      this.editItemDialogOpen = false;
      this.resetEditItemForm();
      return;
    }

    this.#editItemOpening = item;
    let props = {
      editingItem: item,
      itemDescription: item.description,
      itemDate: item.date,
      itemPayerName: item.communityPayerName,
      editItemDialogOpen: true,
    };
    if (this.features.sapFinance && isEmpty(item.get('ledgerItem.id'))) {
      // A belongsTo takes a record, not the async proxy. Load the records
      // before the dialog opens.
      [props.itemCostCentre, props.itemInternalOrder] = await Promise.all([
        item.costCentre,
        item.internalOrder,
      ]);
      if (this.#editItemOpening !== item) {
        // Staff opened another item while this one loaded.
        return;
      }
    }
    this.setProperties(props);
  }

  @action
  editItem() {
    this.itemEditInProgress = true;
    this.editingItem.setProperties({
      description: this.itemDescription,
      date: this.itemDate,
      communityPayerName: this.itemPayerName,
    });
    if (
      this.features.sapFinance &&
      isEmpty(this.get('editingItem.ledgerItem.id'))
    ) {
      this.editingItem.setProperties({
        costCentre: this.itemCostCentre,
        internalOrder: this.itemInternalOrder,
      });
    }

    this.editingItem
      .save()
      .then(() => {
        this.paperToaster.show(`Line Item updated successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.editItemDialogOpen = false;
        this.resetEditItemForm();
      })
      .catch(() => {
        this.paperToaster.show('Error updating Line Item!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.itemEditInProgress = false;
      });
  }

  @action
  toggleAddItemDialog() {
    this.errorMessage = '';
    this.toggleProperty('addItemDialogOpen');
    if (!this.addItemDialogOpen) {
      this.resetAddItemForm();
    }
  }

  @action
  toggleReduceDialog(item) {
    this.errorMessage = '';
    this.toggleProperty('reduceDialogOpen');
    if (!this.reduceDialogOpen) {
      this.resetAdjustmentForm();
    } else if (item) {
      this.paymentItem = item;
      this.adjustmentMode = LineItemAdjustmentMode.REDUCE;
      this.adjustBy = 0;
      this.adjustTo = item.displayAmount;
    }
  }

  @action
  reduce() {
    this.adjustmentInProgress = true;
    this.finance
      .adjustLineItem(
        this.paymentItem,
        this.paymentComment,
        this.paymentDate,
        this.adjustmentMode,
        this.adjustTo,
        this.adjustBy,
      )
      .then(() => {
        this.paperToaster.show(`Reduction applied successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.paymentItem.reload();
        // Explicitly reload all transactions for this line item
        // So we keep our Receipt and Other Credit values up-to-date
        this.store.query('finance/transaction', {
          lineItemId: this.paymentItem.id,
        });
        this.reduceDialogOpen = false;
        this.resetAdjustmentForm();
      })
      .catch(() => {
        this.paperToaster.show('Error applying reduction!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .finally(() => {
        this.adjustmentInProgress = false;
      });
  }

  @action
  toggleIncreaseDialog(item) {
    this.errorMessage = '';
    this.toggleProperty('increaseDialogOpen');
    if (!this.increaseDialogOpen) {
      this.resetAdjustmentForm();
    } else if (item) {
      this.paymentItem = item;
      this.adjustmentMode = LineItemAdjustmentMode.INCREASE;
      this.adjustBy = 0;
      this.adjustTo = item.displayAmount;
    }
  }

  @action
  increase() {
    this.adjustmentInProgress = true;
    this.finance
      .adjustLineItem(
        this.paymentItem,
        this.paymentComment,
        this.paymentDate,
        this.adjustmentMode,
        this.adjustTo,
        this.adjustBy,
      )
      .then(() => {
        this.paperToaster.show(`Increase applied successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.paymentItem.reload();
        // Explicitly reload all transactions for this line item
        // So we keep our Receipt and Other Credit values up-to-date
        this.store.query('finance/transaction', {
          lineItemId: this.paymentItem.id,
        });
        this.increaseDialogOpen = false;
        this.resetAdjustmentForm();
      })
      .catch(() => {
        this.paperToaster.show('Error applying increase!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .finally(() => {
        this.adjustmentInProgress = false;
      });
  }

  resetPaymentForm() {
    this.paymentAmount = null;
    this.paymentMethod = null;
    this.paymentComment = '';
    this.paymentPerson = null;
    this.paymentPersonName = '';
    this.transferAccount = null;
    this.paymentItem = null;
    this.paymentDate = moment().format('YYYY-MM-DD');
    this.paymentChequeNo = '';
    this.paymentChequeBSB = '';
    this.paymentChequeAccountNo = '';
    this.paymentMoneyOrderNumber = '';
    this.paymentMoneyOrderIssuer = '';
    this.paymentMoneyOrderPayee = '';
    this.paymentCommentViolations = [];
  }

  resetVoidWriteoffForm() {
    this.resetPaymentForm();
    this.voidWriteoffType = 'writeoff';
  }

  resetEditItemForm() {
    this.editingItem = null;
    this.itemDescription = '';
    this.itemDate = moment().format('YYYY-MM-DD');
    this.itemPayerName = '';
    this.itemCostCentre = null;
    this.itemInternalOrder = null;
    this.itemDescriptionViolations = [];
  }

  resetAddItemForm() {
    this.resetEditItemForm();
    this.itemLedgerItem = null;
    this.itemLedger = null;
    this.itemAmount = null;
    this.addItemMode = 'ledger';
  }

  resetAdjustmentForm() {
    this.resetPaymentForm();
    this.adjustmentMode = LineItemAdjustmentMode.TO;
    this.adjustBy = 0;
    this.adjustTo = 0;
  }

  reset() {
    this.resetPaymentForm();
    // resetAddItemForm also calls resetEditItemForm, so don't call both.
    this.resetAddItemForm();
    this.addPIADialogOpen = false;
    this.transferPIADialogOpen = false;
    this.transferPIAToSiblingDialogOpen = false;
    this.addPaymentDialogOpen = false;
    this.discountDialogOpen = false;
    this.voidWriteoffDialogOpen = false;
    this.filter = 'unpaid';
    this.currentYear = moment().format('YYYY');
  }
}
